<template>
  <div class="server-settings">
    <div class="section-header">
      <div class="section-header__left">
        <div class="section-header__icon">
          <i class="el-icon-connection"></i>
        </div>
        <div>
          <h2 class="section-header__title">{{ $t('settings.sectionLDAP') }}</h2>
          <p class="section-header__subtitle">
            {{ adConnections.length }}
            {{
              adConnections.length === 1
                ? $t('settings.connectionsSingular')
                : $t('settings.connectionsPlural')
            }}
          </p>
        </div>
      </div>
      <div class="section-header__actions">
        <el-button
          v-if="!isSuperAdmin"
          class="btn-add"
          type="primary"
          icon="el-icon-plus"
          :loading="loading"
          @click="onCreateConnectionClick()">
          {{ $t('settings.addLdapServer') }}
        </el-button>
        <base-refresh-button
          :loading="tableLoading"
          @click="loadServers"/>
      </div>
    </div>

    <div class="table-card">
      <base-table
        :data="adConnections"
        :loading="tableLoading"
        :empty-text="$t('settings.noConnections')"
        row-class-name="ad-connection-table-row"
        class="connections-table"
        @row-click="onRowClick">

        <el-table-column :label="$t('settings.adColServer')" width="440">
          <template v-slot="scope">
            <div class="cell-url">
              <span class="cell-url__badge"><i class="el-icon-connection"></i></span>
              <span class="cell-url__text">{{ scope.row.url }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column v-if="isSuperAdmin" :label="$t('settings.tenant.name')" min-width="200">
          <template v-slot="scope">
            <span class="cell-user">{{ scope.row.tenant ? scope.row.tenant.name : '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column :label="$t('settings.adColLoginDomain')" width="150">
          <template v-slot="scope">
            <div class="cell-domain">
              <el-tag size="small" type="info" class="domain-tag">{{ scope.row.domain }}</el-tag>
            </div>
          </template>
        </el-table-column>

        <el-table-column :label="$t('settings.user')" min-width="180">
          <template v-slot="scope">
            <span class="cell-user">{{ scope.row.syncUser }}</span>
          </template>
        </el-table-column>

        <el-table-column :label="$t('settings.synchronizedAccounts')" width="180" align="center" header-align="center">
          <template v-slot="scope">
            <el-tag size="small" type="info" class="sync-tag">
              <i class="el-icon-user sync-tag__icon"></i>{{ syncedCount(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column width="110" align="center" header-align="center" label="">
          <template v-slot="scope">
            <el-tooltip :content="$t('settings.adConnections.edit')" placement="top">
              <el-button
                circle
                size="small"
                type="primary"
                icon="el-icon-edit"
                @click.stop="onEditClick(scope.row)"/>
            </el-tooltip>
            <el-tooltip :content="$t('base.delete')" placement="top">
              <el-button
                circle
                size="small"
                type="danger"
                icon="el-icon-delete"
                @click.stop="onAdConnectionDeleteIntentionClick(scope.row)"/>
            </el-tooltip>
          </template>
        </el-table-column>
      </base-table>
    </div>

    <!-- Create / Edit connection dialog -->
    <el-dialog
      :visible.sync="showConnectionModal"
      @close="onDialogClose()"
      width="540px"
      custom-class="connection-dialog">

      <div slot="title" class="dlg-head">
        <div class="dlg-head__icon"><i class="el-icon-connection"></i></div>
        <div>
          <div class="dlg-head__title">
            {{ isEdit ? $t('settings.editLdapServerConnection') : $t('settings.addLdapServer') }}
          </div>
          <div class="dlg-head__subtitle">
            {{ isEdit ? $t('settings.adConnections.editSubtitle') : $t('settings.adConnections.createSubtitle') }}
          </div>
        </div>
      </div>

      <div class="dialog-body" v-if="currentAdConnectionCopy">
        <el-form
          @submit.native.prevent="onConnectionFormSubmit"
          :model="currentAdConnectionCopy"
          ref="connection-form"
          label-position="top"
          class="connection-form">

          <el-form-item
            :label="$t('settings.adColServer')"
            prop="url"
            :rules="[{ required: true, message: $t('settings.customerRules.url'), trigger: 'blur' }]">
            <el-input
              v-model="currentAdConnectionCopy.url"
              placeholder="ldaps://…:636">
            </el-input>
            <p v-if="!isEdit" class="field-hint">{{ $t('settings.adConnections.serverHint') }}</p>
          </el-form-item>

          <el-form-item
            :label="$t('settings.adColLoginDomain')"
            prop="domain"
            :rules="[{ required: true, message: $t('settings.accountRules.loginDomain'), trigger: 'blur' }]">
            <el-input
              v-model="currentAdConnectionCopy.domain"
              placeholder="z. B. nwz">
            </el-input>
          </el-form-item>

          <el-form-item
            :label="$t('settings.user')"
            prop="syncUser"
            :rules="[{ required: true, message: $t('settings.customerRules.user'), trigger: 'blur' }]">
            <el-input
              v-model="currentAdConnectionCopy.syncUser"
              placeholder="dienst.konto@domain.de">
            </el-input>
          </el-form-item>

          <el-form-item
            :label="$t('settings.password')"
            prop="syncPassword"
            :rules="[{ required: !isEdit, message: $t('settings.customerRules.password'), trigger: 'blur' }]">

            <!-- Edit mode -->
            <div
              v-if="isEdit && !passwordEditing"
              class="pw-display"
              tabindex="0"
              @click="startPasswordEdit"
              @focus="startPasswordEdit">
              <span class="pw-display__dots">••••••••••</span>
            </div>

            <!-- Create mode-->
            <el-input
              v-else
              ref="passwordInput"
              v-model="currentAdConnectionCopy.syncPassword"
              :placeholder="isEdit ? $t('settings.adConnections.newPasswordPlaceholder') : '••••••••••'"
              :show-password="!isEdit">
            </el-input>

            <p v-if="isEdit && !passwordEditing" class="field-hint">
              {{ $t('settings.adConnections.passwordEditHint') }}
            </p>
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer" class="dialog-footer dialog-footer--split">
        <span class="dialog-footer__left">
          <base-test-connection-button
            :validate="() => $refs['connection-form'].validate()"
            :request="validateLdapServerRequest"
            success-key="connected"
            :fail-message="$t('settings.adConnections.ldapServerErrors.connectionFailed')"
            :error-formatter="serverErrorMessage"/>
        </span>
        <span class="dialog-footer__right">
          <el-button @click="showConnectionModal = false" class="btn-cancel">
            {{ $t('base.cancel') }}
          </el-button>
          <el-button type="primary" @click="onConnectionFormSubmit" :loading="loading" class="btn-save">
            <i class="el-icon-check"></i>
            {{ $t('settings.save') }}
          </el-button>
        </span>
      </span>
    </el-dialog>

    <!-- Delete connection dialog -->
    <base-delete-dialog
      :visible.sync="showDeleteConnectionModal"
      :title="$t('settings.adConnections.deleteConnectionTitle')"
      :loading="loading"
      @confirm="onConnectionDeleteConfirmation">
      <template v-if="currentAdConnectionForDeletion">
        {{
          $t('settings.adConnections.deleteConnectionInfoText', {
            synced: syncedCount(currentAdConnectionForDeletion)
          })
        }}
      </template>
    </base-delete-dialog>

    <!-- Entra -->
    <div class="section-header entra-header">
      <div class="section-header__left">
        <div class="section-header__icon section-header__icon--entra">
          <i class="el-icon-cloudy"></i>
        </div>
        <div>
          <h2 class="section-header__title">{{ $t('settings.sectionEntra') }}</h2>
          <p class="section-header__subtitle">
            {{ entraConnections.length }}
            {{
              entraConnections.length === 1
                ? $t('settings.connectionsSingular')
                : $t('settings.connectionsPlural')
            }}
          </p>
        </div>
      </div>
      <div class="section-header__actions">
        <el-button
          v-if="!isSuperAdmin"
          class="btn-add"
          type="primary"
          icon="el-icon-plus"
          :loading="entraLoading"
          @click="onCreateEntraConnectionClick()">
          {{ $t('settings.addEntraConnection') }}
        </el-button>
        <base-refresh-button
          :loading="entraTableLoading"
          @click="loadEntraConnections"/>
      </div>
    </div>

    <div class="table-card">
      <base-table
        :data="entraConnections"
        :loading="entraTableLoading"
        :empty-text="$t('settings.noEntraConnections')"
        row-class-name="ad-connection-table-row"
        class="connections-table"
        @row-click="onEntraRowClick">

        <el-table-column :label="$t('settings.entraTenantNameColumn')" width="440">
          <template v-slot="scope">
            <div class="cell-url">
              <span class="cell-url__badge cell-url__badge--entra"><i class="el-icon-cloudy"></i></span>
              <span class="cell-url__text">{{ scope.row.name || '—' }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column v-if="isSuperAdmin" :label="$t('settings.tenant.name')" min-width="200">
          <template v-slot="scope">
            <span class="cell-user">{{ scope.row.tenant ? scope.row.tenant.name : '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column :label="$t('settings.entraTenantId')" min-width="180">
          <template v-slot="scope">
            <span class="cell-user">{{ scope.row.entraTenantId }}</span>
          </template>
        </el-table-column>

        <el-table-column width="150" label=""/>

        <el-table-column :label="$t('settings.synchronizedAccounts')" width="180" align="center" header-align="center">
          <template v-slot="scope">
            <el-tag size="small" type="info" class="sync-tag">
              <i class="el-icon-user sync-tag__icon"></i>{{ syncedCount(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column width="110" align="center" header-align="center" label="">
          <template v-slot="scope">
            <el-tooltip :content="$t('settings.adConnections.edit')" placement="top">
              <el-button
                circle
                size="small"
                type="primary"
                icon="el-icon-edit"
                @click.stop="onEntraEditClick(scope.row)"/>
            </el-tooltip>
            <el-tooltip :content="$t('base.delete')" placement="top">
              <el-button
                circle
                size="small"
                type="danger"
                icon="el-icon-delete"
                @click.stop="onEntraConnectionDeleteIntentionClick(scope.row)"/>
            </el-tooltip>
          </template>
        </el-table-column>
      </base-table>
    </div>

    <!-- Registration: Only Super Admins can edit -->
    <div class="registration-card">
      <div class="registration-card__head">
        <div class="registration-card__icon"><i class="el-icon-key"></i></div>
        <div class="registration-card__title-wrap">
          <div class="registration-card__title">{{ $t('settings.entraRegistrationTitle') }}</div>
          <div class="registration-card__subtitle">{{ $t('settings.entraRegistrationSubtitle') }}</div>
        </div>
      </div>

      <el-form
        v-if="entraRegistrationCopy"
        :model="entraRegistrationCopy"
        ref="registration-form"
        label-position="top"
        class="registration-form">

        <el-form-item
          :label="$t('settings.entraAppId')"
          prop="appId"
          :rules="isSuperAdmin ? [{ required: true, message: $t('settings.entraRules.appId'), trigger: 'blur' }] : []">
          <el-input
            v-model="entraRegistrationCopy.appId"
            :readonly="!isSuperAdmin"
            :placeholder="$t('settings.entraAppId')">
          </el-input>
        </el-form-item>

        <el-form-item
          :label="$t('settings.entraClientSecret')"
          prop="clientSecret"
          :rules="isSuperAdmin ? [{ required: !entraRegistrationCopy.id, message: $t('settings.entraRules.clientSecret'), trigger: 'blur' }] : []">

          <!-- Super-Admin: edit mode -->
          <div
            v-if="isSuperAdmin && entraRegistrationCopy.id && !entraSecretEditing"
            class="pw-display"
            tabindex="0"
            @click="startEntraSecretEdit"
            @focus="startEntraSecretEdit">
            <span class="pw-display__dots">••••••••••</span>
          </div>

          <el-input
            v-else-if="isSuperAdmin"
            ref="entraSecretInput"
            v-model="entraRegistrationCopy.clientSecret"
            :placeholder="entraRegistrationCopy.id ? $t('settings.adConnections.newPasswordPlaceholder') : '••••••••••'"
            show-password>
          </el-input>

          <div v-else class="pw-display pw-display--readonly">
            <span class="pw-display__dots">••••••••••</span>
          </div>

          <p v-if="isSuperAdmin && entraRegistrationCopy.id && !entraSecretEditing" class="field-hint">
            {{ $t('settings.adConnections.passwordEditHint') }}
          </p>
        </el-form-item>
      </el-form>

      <div v-if="isSuperAdmin" class="registration-card__actions">
        <el-button type="primary" size="small" :loading="entraRegistrationLoading" @click="onEntraRegistrationSubmit">
          <i class="el-icon-check"></i> {{ $t('settings.save') }}
        </el-button>
      </div>
    </div>

    <!-- Create / Edit Entra connection dialog -->
    <el-dialog
      :visible.sync="showEntraConnectionModal"
      @close="onEntraDialogClose()"
      width="480px"
      custom-class="connection-dialog">

      <div slot="title" class="dlg-head">
        <div class="dlg-head__icon dlg-head__icon--entra"><i class="el-icon-cloudy"></i></div>
        <div>
          <div class="dlg-head__title">
            {{ isEntraEdit ? $t('settings.entraServerEdit') : $t('settings.addEntraConnection') }}
          </div>
          <div class="dlg-head__subtitle">
            {{
              isEntraEdit ? $t('settings.entraConnectionEditSubtitle') : $t('settings.entraConnectionCreateSubtitle')
            }}
          </div>
        </div>
      </div>

      <div class="dialog-body" v-if="currentEntraConnectionCopy">
        <el-form
          @submit.native.prevent="onEntraConnectionFormSubmit"
          :model="currentEntraConnectionCopy"
          ref="entra-connection-form"
          label-position="top"
          class="connection-form">

          <el-form-item
            :label="$t('settings.entraName')"
            prop="name"
            :rules="[{ required: true, message: $t('settings.entraRules.name'), trigger: 'blur' }]">
            <el-input
              v-model="currentEntraConnectionCopy.name"
              :placeholder="$t('settings.entraNamePlaceholder')">
            </el-input>
          </el-form-item>

          <el-form-item
            :label="$t('settings.entraTenantId')"
            prop="entraTenantId"
            :rules="[{ required: true, message: $t('settings.entraRules.tenantId'), trigger: 'blur' }]">
            <el-input
              v-model="currentEntraConnectionCopy.entraTenantId"
              placeholder="72f988bf-86f1-41af-91ab-2d7cd011db47">
            </el-input>
            <p class="field-hint">{{ $t('settings.entraTenantIdHint') }}</p>
          </el-form-item>

          <entra-consent-url :tenant-id="currentEntraConnectionCopy.entraTenantId"/>
        </el-form>
      </div>

      <span slot="footer" class="dialog-footer dialog-footer--split">
        <span class="dialog-footer__left">
          <base-test-connection-button
            :validate="() => $refs['entra-connection-form'].validate()"
            :request="validateEntraConnectionRequest"
            success-key="connected"
            :fail-message="$t('settings.entraErrors.connectionFailed')"
            :error-formatter="entraConnectionTestError"/>
        </span>
        <span class="dialog-footer__right">
          <el-button @click="showEntraConnectionModal = false" class="btn-cancel">
            {{ $t('base.cancel') }}
          </el-button>
          <el-button type="primary" @click="onEntraConnectionFormSubmit" :loading="entraLoading" class="btn-save">
            <i class="el-icon-check"></i>
            {{ $t('settings.save') }}
          </el-button>
        </span>
      </span>
    </el-dialog>

    <!-- Delete Entra connection dialog -->
    <base-delete-dialog
      :visible.sync="showEntraDeleteConnectionModal"
      :title="$t('settings.entraDeleteConnectionTitle')"
      :loading="entraLoading"
      @confirm="onEntraConnectionDeleteConfirmation">
      <template v-if="currentEntraConnectionForDeletion">
        {{
          $t('settings.adConnections.deleteConnectionInfoText', {
            synced: syncedCount(currentEntraConnectionForDeletion)
          })
        }}
      </template>
    </base-delete-dialog>

    <!-- Warning before a change that affects synchronisation -->
    <base-warning-dialog
      :visible="showChangeWarningModal"
      :title="$t('settings.changeWarning.title')"
      :confirm-label="$t('settings.changeWarning.confirm')"
      @update:visible="onChangeWarningVisibilityChange"
      @confirm="onChangeWarningConfirmation">
      {{ changeWarningText }}
    </base-warning-dialog>
  </div>
</template>

<script>
import {ldapServerService} from '@/services/LdapServerService.class';
import {getEntraErrorMessage} from '@/helper/EntraErrorHelper';
import {entraRegistrationService} from '@/services/EntraRegistrationService.class';
import {entraConnectionService} from '@/services/EntraConnectionService.class';
import BaseTable from '@/components/common/BaseTable';
import BaseDeleteDialog from '@/components/common/BaseDeleteDialog';
import BaseWarningDialog from '@/components/common/BaseWarningDialog';
import BaseTestConnectionButton from '@/components/common/BaseTestConnectionButton';
import BaseRefreshButton from '@/components/common/BaseRefreshButton';
import EntraConsentUrl from '@/components/settings/EntraConsentURl';
import {createNamespacedHelpers} from 'vuex';

const authStateMappers = createNamespacedHelpers('auth');

export default {
  name: 'ServerSettings',
  components: {
    BaseTable,
    BaseDeleteDialog,
    BaseWarningDialog,
    BaseTestConnectionButton,
    BaseRefreshButton,
    EntraConsentUrl
  },
  data() {
    return {
      adConnections: [],
      loading: false,
      tableLoading: false,
      currentAdConnectionCopy: {},
      currentAdConnectionOriginal: null,
      showConnectionModal: false,
      showDeleteConnectionModal: false,
      currentAdConnectionForDeletion: null,
      passwordEditing: false,
      entraConnections: [],
      entraLoading: false,
      entraTableLoading: false,
      currentEntraConnectionCopy: {},
      currentEntraConnectionOriginal: null,
      showEntraConnectionModal: false,
      showEntraDeleteConnectionModal: false,
      currentEntraConnectionForDeletion: null,
      entraRegistrationCopy: null,
      entraRegistrationLoading: false,
      entraSecretEditing: false,
      showChangeWarningModal: false,
      changeWarningEntity: null,
      pendingSubmit: null,
    };
  },
  computed: {
    isEdit() {
      return !!(this.currentAdConnectionCopy && this.currentAdConnectionCopy.id);
    },
    isEntraEdit() {
      return !!(this.currentEntraConnectionCopy && this.currentEntraConnectionCopy.id);
    },
    changeWarningText() {
      const entity = this.changeWarningEntity;
      if (!entity) return '';

      const providerCount = this.providerCount(entity);
      const accountCount = this.syncedCount(entity);

      const providers = providerCount === 1
        ? this.$t('settings.changeWarning.providersSingular')
        : this.$t('settings.changeWarning.providersPlural', {count: providerCount});
      const accounts = accountCount === 1
        ? this.$t('settings.changeWarning.accountsSingular')
        : this.$t('settings.changeWarning.accountsPlural', {count: accountCount});

      return this.$t('settings.changeWarning.text', {providers, accounts});
    },
    ...authStateMappers.mapGetters({isSuperAdmin: 'isSuperAdmin'}),
  },
  mounted() {
    this.loadServers();
    this.loadEntraConnections();
    this.loadEntraRegistration();
  },
  methods: {
    syncedCount(row) {
      return row && row.synchronizedUserCount != null ? row.synchronizedUserCount : 0;
    },
    providerCount(row) {
      return row && row.remoteAccountProviderCount != null ? row.remoteAccountProviderCount : 0;
    },
    isCriticalChange(entity) {
      return this.providerCount(entity) > 0 || this.syncedCount(entity) > 0;
    },
    onChangeWarningConfirmation() {
      this.showChangeWarningModal = false;

      const submit = this.pendingSubmit;
      this.pendingSubmit = null;
      if (submit) submit();
    },
    onChangeWarningVisibilityChange(visible) {
      this.showChangeWarningModal = visible;
      if (!visible) {
        this.pendingSubmit = null;
        this.changeWarningEntity = null;
      }
    },
    loadServers() {
      this.tableLoading = true;
      ldapServerService.getAll()
        .then((response) => {
          this.adConnections = response.data;
        })
        .catch(() => {
          this.$message.error(this.$t('settings.genericHttpError'));
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    onRowClick(adConnection) {
      this.openEditDialog(adConnection);
    },
    onEditClick(adConnection) {
      this.openEditDialog(adConnection);
    },
    openEditDialog(adConnection) {
      this.currentAdConnectionCopy = {...adConnection, syncPassword: ''};
      this.currentAdConnectionOriginal = {...adConnection};
      this.passwordEditing = false;
      this.showConnectionModal = true;
    },
    onCreateConnectionClick() {
      this.currentAdConnectionCopy = {};
      this.currentAdConnectionOriginal = null;
      this.passwordEditing = true;
      this.showConnectionModal = true;
    },
    startPasswordEdit() {
      this.passwordEditing = true;
      this.$set(this.currentAdConnectionCopy, 'syncPassword', '');
      this.$nextTick(() => {
        const input = this.$refs.passwordInput;
        if (input && input.focus) input.focus();
      });
    },
    onDialogClose() {
      this.currentAdConnectionCopy = null;
      this.currentAdConnectionOriginal = null;
      this.currentAdConnectionForDeletion = null;
      this.passwordEditing = false;
    },
    onAdConnectionDeleteIntentionClick(adConnection) {
      this.currentAdConnectionForDeletion = adConnection;
      this.showDeleteConnectionModal = true;
    },
    serverErrorMessage(e) {
      const status = e && e.response && e.response.status;
      if (status === 409) {
        return this.$t('settings.adConnections.ldapServerErrors.alreadyExists');
      }
      if (status === 400 || status === 502 || status === 503 || status === 504) {
        return this.$t('settings.adConnections.ldapServerErrors.connectionFailed');
      }

      return this.$t('settings.adConnections.ldapServerErrors.generic');
    },
    entraErrorMessage(e, context = 'connection') {
      return getEntraErrorMessage(this.$t.bind(this), e, context);
    },
    validateLdapServerRequest() {
      return ldapServerService.validate({
        id: this.currentAdConnectionCopy.id,
        url: this.currentAdConnectionCopy.url,
        domain: this.currentAdConnectionCopy.domain,
        syncUser: this.currentAdConnectionCopy.syncUser,
        syncPassword: this.currentAdConnectionCopy.syncPassword,
      });
    },
    async onConnectionFormSubmit() {
      try {
        await this.$refs['connection-form'].validate();
      } catch (e) {
        return;
      }

      const original = this.currentAdConnectionOriginal;
      const urlChanged = !!(original && original.url !== this.currentAdConnectionCopy.url);

      if (urlChanged && this.isCriticalChange(original)) {
        this.changeWarningEntity = original;
        this.pendingSubmit = this.saveConnection;
        this.showChangeWarningModal = true;
        return;
      }

      this.saveConnection();
    },
    async saveConnection() {
      this.loading = true;
      try {
        const payload = {...this.currentAdConnectionCopy};

        if (payload.id) {
          if (!payload.syncPassword) {
            delete payload.syncPassword;
          }
          await ldapServerService.patch(payload);
          this.$message.success(this.$t('settings.adConnectionUpdated'));
        } else {
          await ldapServerService.create(payload);
          this.$message.success(this.$t('settings.adConnectionAdded'));
        }

        this.showConnectionModal = false;
        this.loadServers();
      } catch (e) {
        this.$message.error(this.serverErrorMessage(e));
      } finally {
        this.loading = false;
      }
    },
    async onConnectionDeleteConfirmation() {
      try {
        this.loading = true;
        await ldapServerService.delete(this.currentAdConnectionForDeletion.id);
        this.$message.success(this.$t('settings.adConnectionDeleted'));
        this.showDeleteConnectionModal = false;
        this.loadServers();
      } catch (e) {
        this.$message.error(this.$t('settings.genericHttpError'));
      } finally {
        this.loading = false;
      }
    },

    // Entra Registration
    loadEntraRegistration() {
      entraRegistrationService.getDefault()
        .then((response) => {
          this.entraRegistrationCopy = {...response.data, clientSecret: ''};
        })
        .catch(() => {
          this.entraRegistrationCopy = this.isSuperAdmin ? {appId: '', clientSecret: ''} : null;
        });
    },
    startEntraSecretEdit() {
      if (!this.isSuperAdmin) return;
      this.entraSecretEditing = true;
      this.$set(this.entraRegistrationCopy, 'clientSecret', '');
      this.$nextTick(() => {
        const input = this.$refs.entraSecretInput;
        if (input && input.focus) input.focus();
      });
    },
    async onEntraRegistrationSubmit() {
      if (!this.isSuperAdmin) return;
      try {
        await this.$refs['registration-form'].validate();
      } catch (e) {
        return;
      }
      this.entraRegistrationLoading = true;
      try {
        const payload = {...this.entraRegistrationCopy};
        if (payload.id) {
          if (!payload.clientSecret) {
            delete payload.clientSecret;
          }
          const id = payload.id;
          delete payload.id;
          await entraRegistrationService.patch({id, appId: payload.appId, clientSecret: payload.clientSecret});
        } else {
          delete payload.id;
          await entraRegistrationService.create(payload);
        }
        this.$message.success(this.$t('settings.entraRegistrationUpdated'));
        this.entraSecretEditing = false;
        this.loadEntraRegistration();
      } catch (e) {
        this.$message.error(this.entraErrorMessage(e));
      } finally {
        this.entraRegistrationLoading = false;
      }
    },

    // Entra: Connections
    loadEntraConnections() {
      this.entraTableLoading = true;
      entraConnectionService.getAll()
        .then((response) => {
          this.entraConnections = response.data;
        })
        .catch((e) => {
          this.$message.error(this.entraErrorMessage(e));
        })
        .finally(() => {
          this.entraTableLoading = false;
        });
    },
    onEntraRowClick(entraConnection) {
      this.openEntraEditDialog(entraConnection);
    },
    onEntraEditClick(entraConnection) {
      this.openEntraEditDialog(entraConnection);
    },
    openEntraEditDialog(entraConnection) {
      this.currentEntraConnectionCopy = {...entraConnection};
      this.currentEntraConnectionOriginal = {...entraConnection};
      this.showEntraConnectionModal = true;
    },
    onCreateEntraConnectionClick() {
      this.currentEntraConnectionCopy = {};
      this.currentEntraConnectionOriginal = null;
      this.showEntraConnectionModal = true;
    },
    onEntraDialogClose() {
      this.currentEntraConnectionCopy = null;
      this.currentEntraConnectionOriginal = null;
      this.currentEntraConnectionForDeletion = null;
    },
    onEntraConnectionDeleteIntentionClick(entraConnection) {
      this.currentEntraConnectionForDeletion = entraConnection;
      this.showEntraDeleteConnectionModal = true;
    },
    entraConnectionTestError(e) {
      if (e && e.message === 'REGISTRATION_MISSING') {
        return this.$t('settings.entraErrors.registrationMissing');
      }
      return this.entraErrorMessage(e, 'connection');
    },
    async validateEntraConnectionRequest() {
      let registrationId = this.currentEntraConnectionCopy.registrationId
        || (this.entraRegistrationCopy && this.entraRegistrationCopy.id);

      if (!registrationId) {
        try {
          const {data: registration} = await entraRegistrationService.getDefault();
          registrationId = registration.id;
        } catch (e) {
          throw new Error('REGISTRATION_MISSING');
        }
      }

      return entraConnectionService.validate({
        id: this.currentEntraConnectionCopy.id,
        registrationId,
        entraTenantId: this.currentEntraConnectionCopy.entraTenantId,
      });
    },
    async onEntraConnectionFormSubmit() {
      try {
        await this.$refs['entra-connection-form'].validate();
      } catch (e) {
        return;
      }

      const original = this.currentEntraConnectionOriginal;
      const tenantChanged = !!(original && original.entraTenantId !== this.currentEntraConnectionCopy.entraTenantId);

      if (tenantChanged && this.isCriticalChange(original)) {
        this.changeWarningEntity = original;
        this.pendingSubmit = this.saveEntraConnection;
        this.showChangeWarningModal = true;
        return;
      }

      this.saveEntraConnection();
    },
    async saveEntraConnection() {
      this.entraLoading = true;
      try {
        const payload = {...this.currentEntraConnectionCopy};

        if (payload.id) {
          await entraConnectionService.patch(payload);
          this.$message.success(this.$t('settings.entraConnectionUpdated'));
        } else {
          let registration;
          try {
            ({data: registration} = await entraRegistrationService.getDefault());
          } catch (e) {
            this.$message.error(this.$t('settings.entraErrors.registrationMissing'));
            return;
          }
          payload.registrationId = registration.id;
          await entraConnectionService.create(payload);
          this.$message.success(this.$t('settings.entraConnectionAdded'));
        }

        this.showEntraConnectionModal = false;
        this.loadEntraConnections();
      } catch (e) {
        this.$message.error(this.entraErrorMessage(e));
      } finally {
        this.entraLoading = false;
      }
    },
    async onEntraConnectionDeleteConfirmation() {
      try {
        this.entraLoading = true;
        await entraConnectionService.delete(this.currentEntraConnectionForDeletion.id);
        this.$message.success(this.$t('settings.entraConnectionDeleted'));
        this.showEntraDeleteConnectionModal = false;
        this.loadEntraConnections();
      } catch (e) {
        this.$message.error(this.entraErrorMessage(e));
      } finally {
        this.entraLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.connection-dialog {
  border-radius: 14px !important;
  overflow: hidden;

  .el-dialog__header {
    padding: 20px 24px 18px;
    border-bottom: 1px solid #f0f0f0;

    .el-dialog__headerbtn {
      top: 22px;
      right: 22px;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #f0f0f0;
    background: #fafbfd;
  }
}

.ad-connection-table-row {
  cursor: pointer;
  transition: background 0.15s ease;
}
</style>

<style scoped lang="scss">
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, #409EFF 0%, #2d7dd2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: #fff;
      font-size: 18px;
    }
  }

  &__title {
    margin: 0 0 2px;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.btn-add {
  border-radius: 8px;
  font-weight: 500;
  padding: 9px 18px;
  height: auto;
  letter-spacing: 0.01em;
}

.table-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  margin-bottom: 20px;
}

.connections-table {
  width: 100%;

  :deep(.cell) {
    padding: 0 16px;
  }
}

.cell-url {
  display: flex;
  align-items: center;
  gap: 12px;

  &__badge {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #eef4ff;
    color: var(--primary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 15px;
    }
  }

  &__text {
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 12.5px;
    color: #444;
  }
}

.domain-tag {
  font-weight: 500;
  border-radius: 6px;
}

.cell-user {
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 12.5px;
  color: #555;
}

.sync-tag {
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  border-radius: 6px;

  &__icon {
    margin-right: 4px;
    font-size: 12px;
    vertical-align: -1px;
  }
}

.dlg-head {
  display: flex;
  align-items: center;
  gap: 14px;

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #eef4ff;
    color: var(--primary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 19px;
    }
  }

  &__title {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.25;
  }

  &__subtitle {
    font-size: 12.5px;
    color: var(--text-secondary);
    margin-top: 2px;
  }
}

.dialog-body {
  padding: 0;
}

.connection-form {
  :deep(.el-form-item__label) {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    line-height: 1.4;
    padding-bottom: 6px;
  }

  :deep(.el-input__inner) {
    border-radius: 8px;
    border-color: var(--border);
    height: 42px;
    font-size: 13.5px;
    color: var(--text-primary);
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus {
      border-color: var(--primary);
      box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.12);
    }
  }

  :deep(.el-form-item) {
    margin-bottom: 20px;
  }
}

.field-hint {
  margin: 6px 0 0;
  font-size: 11.5px;
  color: #9aa0b0;
  line-height: 1.4;
}

.pw-display {
  height: 42px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 14px;
  cursor: text;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover,
  &:focus {
    border-color: var(--primary);
    outline: none;
  }

  &:focus {
    box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.12);
  }

  &__dots {
    letter-spacing: 3px;
    color: #b8bdcc;
    font-size: 14px;
    user-select: none;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;

  &--split {
    justify-content: space-between;
    align-items: flex-start;
  }

  &__left {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    min-height: 40px;
    justify-content: center;
  }

  &__right {
    display: inline-flex;
    gap: 10px;
    flex-shrink: 0;
  }

  .btn-cancel {
    border-radius: 8px;
    color: var(--text-secondary);
  }

  .btn-save {
    border-radius: 8px;
    font-weight: 500;
    min-width: 100px;
  }
}

.entra-header {
  margin-top: 32px;
}

.section-header__icon--entra {
  background: linear-gradient(135deg, var(--entra) 0%, var(--entra-dark) 100%);
}

.cell-url__badge--entra {
  background: var(--entra-bg);
  color: var(--entra-dark);
}

.dlg-head__icon--entra {
  background: var(--entra-bg);
  color: var(--entra-dark);
}

.registration-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 20px 22px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);

  &__head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 18px;
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: var(--entra-bg);
    color: var(--entra-dark);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 18px;
    }
  }

  &__title-wrap {
    flex-grow: 1;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 12px;
    color: var(--text-secondary);
    margin-top: 2px;
  }

  &__badge {
    font-size: 11.5px;
    padding: 4px 10px;
    border-radius: 20px;
    font-weight: 500;
    flex-shrink: 0;

    &--edit {
      background: #fdf1dc;
      color: #96690c;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 4px;
  }
}

.registration-form {
  max-width: 440px;

  :deep(.el-form-item__label) {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    line-height: 1.4;
    padding-bottom: 6px;
  }

  :deep(.el-input__inner) {
    border-radius: 8px;
    border-color: var(--border);
    height: 42px;
    font-size: 13.5px;
    color: var(--text-primary);
  }

  :deep(.el-input.is-disabled .el-input__inner),
  :deep(.el-input__inner[readonly]) {
    background: #fafbfd;
    color: var(--text-secondary);
    cursor: default;
  }

  :deep(.el-form-item) {
    margin-bottom: 18px;
  }
}

.pw-display--readonly {
  cursor: default;
  background: #fafbfd;

  &:hover,
  &:focus {
    border-color: var(--border);
    box-shadow: none;
  }
}

</style>
